import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SlidersHorizontalIcon, ArrowUpDownIcon, FilterIcon, XIcon } from 'lucide-react';
import { useHorizontalScroll } from '../lib/hooks/useHorizontalScroll';
import { motion, AnimatePresence } from 'framer-motion';
import { EnhancedProductCard } from '../components/shared/EnhancedProductCard';
import { NowCompactProductCard } from '../components/now/NowCompactProductCard';
import { FilterSidebar } from '../components/category/FilterSidebar';
import { MobileFilterModal } from '../components/category/MobileFilterModal';
import { MobileSortModal } from '../components/category/MobileSortModal';
import { MobileInternalHeader } from '../components/layout/MobileInternalHeader';
import { NowCategoryBrowseHeader } from '../components/now/NowCategoryBrowseHeader';
import { pharmacyCategories } from '../data/categories';
interface CategoryPLPProps {
  categoryName: string;
  categoryDescription: string;
  isNowContext?: boolean;
}
export function CategoryPLP({
  categoryName,
  categoryDescription,
  isNowContext = false
}: CategoryPLPProps) {
  const INITIAL_PRODUCT_COUNT = 12;
  const PRODUCT_LOAD_INCREMENT = 3;
  const [displayedCount, setDisplayedCount] = useState(INITIAL_PRODUCT_COUNT);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showMobileSort, setShowMobileSort] = useState(false);
  const [showFab, setShowFab] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({
    brands: [] as string[],
    priceRange: {
      min: 0,
      max: 5000
    },
    categories: [] as string[],
    needsPrescription: false,
    isExpress: false,
    isGlobal: false,
    offers: [] as string[]
  });
  const [sortBy, setSortBy] = useState('best-match');
  const [nowAvailability, setNowAvailability] = useState<'all' | 'available' | 'limited'>('all');
  const [nowCategoryId, setNowCategoryId] = useState(() => pharmacyCategories.find(category => category.id === categoryName.toLowerCase().replace(/\s+/g, '-'))?.id ?? 'medicines');
  const [nowSubcategory, setNowSubcategory] = useState('All');
  useEffect(() => {
    // Show FAB immediately on mobile
    setShowFab(true);
    const handleScroll = () => {
      // Optional: hide/show based on scroll direction or position if needed
      // For now, keeping it always visible for better UX
      setShowFab(true);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const activeFilters = useMemo(() => {
    const filters: {
      id: string;
      label: string;
      type: string;
    }[] = [];
    selectedFilters.brands.forEach(brand => {
      filters.push({
        id: `brand-${brand}`,
        label: brand,
        type: 'brand'
      });
    });
    selectedFilters.categories.forEach(cat => {
      filters.push({
        id: `cat-${cat}`,
        label: cat,
        type: 'category'
      });
    });
    selectedFilters.offers.forEach(offer => {
      filters.push({
        id: `offer-${offer}`,
        label: offer,
        type: 'offer'
      });
    });
    if (selectedFilters.needsPrescription) {
      filters.push({
        id: 'rx',
        label: 'Prescription Required',
        type: 'rx'
      });
    }
    if (selectedFilters.priceRange.min > 0 || selectedFilters.priceRange.max < 5000) {
      filters.push({
        id: 'price',
        label: `${selectedFilters.priceRange.min} - ${selectedFilters.priceRange.max} SAR`,
        type: 'price'
      });
    }
    return filters;
  }, [selectedFilters]);
  const removeFilter = (filter: {
    id: string;
    label: string;
    type: string;
  }) => {
    if (filter.type === 'brand') {
      setSelectedFilters(prev => ({
        ...prev,
        brands: prev.brands.filter(b => b !== filter.label)
      }));
    } else if (filter.type === 'category') {
      setSelectedFilters(prev => ({
        ...prev,
        categories: prev.categories.filter(c => c !== filter.label)
      }));
    } else if (filter.type === 'offer') {
      setSelectedFilters(prev => ({
        ...prev,
        offers: prev.offers.filter(o => o !== filter.label)
      }));
    } else if (filter.type === 'rx') {
      setSelectedFilters(prev => ({
        ...prev,
        needsPrescription: false
      }));
    } else if (filter.type === 'price') {
      setSelectedFilters(prev => ({
        ...prev,
        priceRange: {
          min: 0,
          max: 5000
        }
      }));
    }
  };
  const clearAllFilters = () => {
    setSelectedFilters({
      brands: [],
      priceRange: {
        min: 0,
        max: 5000
      },
      categories: [],
      needsPrescription: false,
      offers: []
    });
  };
  // Single banner row with 16:9 aspect ratio
  const banners = [{
    img: 'https://images.unsplash.com/photo-1556228578-0d85b1a4d571?w=1200&h=400&fit=crop',
    link: '#',
    alt: 'Category Banner 1'
  }, {
    img: 'https://images.unsplash.com/photo-1556228720-195a672e8a03?w=1200&h=400&fit=crop',
    link: '#',
    alt: 'Category Banner 2'
  }];
  // Mock product data using home page structure
  const products = [{
    id: 1,
    name: 'Bambi Pants Box (Size) 6 X - LARGE * 68',
    brand: 'Bambi',
    price: 113.85,
    originalPrice: 145.0,
    discount: 22,
    rating: 4.8,
    reviewCount: 156,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp',
    isBestSeller: true
  }, {
    id: 2,
    name: 'Fine Baby Junior Jumbo Pack Size 6 Baby Diapers 22 Kg 38 Diapers',
    brand: 'Fine Baby',
    price: 54.51,
    originalPrice: 68.99,
    discount: 21,
    rating: 4.7,
    reviewCount: 203,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 3,
    name: 'Babyjoy Culotte Size 6 Junior Xxl 16+ Kg Giant Pack 50 Diaper Pants',
    brand: 'Babyjoy',
    price: 94.21,
    originalPrice: 120.0,
    discount: 21,
    rating: 4.9,
    reviewCount: 178,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp',
    isNew: true
  }, {
    id: 4,
    name: 'Babyjoy Culotte Size 4 Large Jumbo Pack 8-10 Kg 44 Diaper Pants',
    brand: 'Babyjoy',
    price: 62.81,
    originalPrice: 79.99,
    discount: 21,
    rating: 4.6,
    reviewCount: 142,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 5,
    name: 'Pampers Premium Care Size 5 Junior 11-16 Kg 40 Diapers',
    brand: 'Pampers',
    price: 89.99,
    originalPrice: 110.0,
    discount: 18,
    rating: 4.8,
    reviewCount: 289,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp',
    isBestSeller: true
  }, {
    id: 6,
    name: 'Huggies Ultra Comfort Size 4 Mega Pack 8-14 Kg 66 Diapers',
    brand: 'Huggies',
    price: 78.5,
    originalPrice: 95.0,
    discount: 17,
    rating: 4.7,
    reviewCount: 234,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 7,
    name: 'Pampers Active Baby Size 4 Maxi 9-14 Kg 82 Diapers',
    brand: 'Pampers',
    price: 95.99,
    originalPrice: 115.0,
    discount: 17,
    rating: 4.6,
    reviewCount: 198,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 8,
    name: 'Babyjoy Compressed Size 5 Junior 12-18 Kg 58 Diapers',
    brand: 'Babyjoy',
    price: 72.5,
    originalPrice: 89.99,
    discount: 19,
    rating: 4.5,
    reviewCount: 167,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 9,
    name: 'Fine Baby Smart Lock Size 5 Junior 11-18 Kg 60 Diapers',
    brand: 'Fine Baby',
    price: 68.99,
    originalPrice: 85.0,
    discount: 19,
    rating: 4.4,
    reviewCount: 145,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 10,
    name: 'Huggies Dry Comfort Size 5 Junior 12-22 Kg 44 Diapers',
    brand: 'Huggies',
    price: 82.5,
    originalPrice: 99.99,
    discount: 17,
    rating: 4.7,
    reviewCount: 212,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 11,
    name: 'Pampers Baby Dry Size 3 Midi 6-10 Kg 74 Diapers',
    brand: 'Pampers',
    price: 79.99,
    originalPrice: 95.0,
    discount: 16,
    rating: 4.6,
    reviewCount: 189,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 12,
    name: 'Babyjoy Healthy Skin Size 4 Large 10-18 Kg 62 Diapers',
    brand: 'Babyjoy',
    price: 75.5,
    originalPrice: 92.0,
    discount: 18,
    rating: 4.5,
    reviewCount: 156,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 13,
    name: 'Fine Baby Natural Touch Size 4 Maxi 7-14 Kg 50 Diapers',
    brand: 'Fine Baby',
    price: 58.99,
    originalPrice: 72.0,
    discount: 18,
    rating: 4.4,
    reviewCount: 134,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 14,
    name: 'Huggies Snug & Dry Size 6 Extra Large 16+ Kg 52 Diapers',
    brand: 'Huggies',
    price: 86.5,
    originalPrice: 105.0,
    discount: 18,
    rating: 4.6,
    reviewCount: 178,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 15,
    name: 'Pampers Pants Size 5 Junior 12-17 Kg 48 Pants',
    brand: 'Pampers',
    price: 92.99,
    originalPrice: 112.0,
    discount: 17,
    rating: 4.7,
    reviewCount: 201,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 16,
    name: 'Bambi Nature Size 5 Junior 11-25 Kg 44 Diapers',
    brand: 'Bambi',
    price: 98.5,
    originalPrice: 118.0,
    discount: 17,
    rating: 4.8,
    reviewCount: 167,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 17,
    name: 'Babyjoy Compressed Diamond Pad Size 6 XXL 16+ Kg 40 Diapers',
    brand: 'Babyjoy',
    price: 69.99,
    originalPrice: 85.0,
    discount: 18,
    rating: 4.5,
    reviewCount: 145,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 18,
    name: 'Fine Baby Super Dry Size 3 Medium 4-9 Kg 68 Diapers',
    brand: 'Fine Baby',
    price: 62.5,
    originalPrice: 76.0,
    discount: 18,
    rating: 4.4,
    reviewCount: 123,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 19,
    name: 'Huggies Little Snugglers Size 2 Newborn 3-6 Kg 84 Diapers',
    brand: 'Huggies',
    price: 74.99,
    originalPrice: 90.0,
    discount: 17,
    rating: 4.6,
    reviewCount: 189,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 20,
    name: 'Pampers Swaddlers Size 1 Newborn 2-5 Kg 92 Diapers',
    brand: 'Pampers',
    price: 81.5,
    originalPrice: 98.0,
    discount: 17,
    rating: 4.7,
    reviewCount: 234,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 21,
    name: 'Babyjoy New Born Size 1 Newborn 2-4 Kg 100 Diapers',
    brand: 'Babyjoy',
    price: 65.99,
    originalPrice: 80.0,
    discount: 18,
    rating: 4.5,
    reviewCount: 156,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 22,
    name: 'Fine Baby Diaper Pants Size 5 Junior 12-22 Kg 36 Pants',
    brand: 'Fine Baby',
    price: 56.5,
    originalPrice: 68.0,
    discount: 17,
    rating: 4.4,
    reviewCount: 134,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 23,
    name: 'Huggies Pull-Ups Size 4 Large 8-15 Kg 42 Training Pants',
    brand: 'Huggies',
    price: 77.99,
    originalPrice: 94.0,
    discount: 17,
    rating: 4.6,
    reviewCount: 178,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 24,
    name: 'Pampers Cruisers Size 4 Maxi 9-14 Kg 70 Diapers',
    brand: 'Pampers',
    price: 88.5,
    originalPrice: 106.0,
    discount: 17,
    rating: 4.7,
    reviewCount: 201,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 25,
    name: 'Bambi Premium Care Size 4 Maxi 8-16 Kg 56 Diapers',
    brand: 'Bambi',
    price: 102.99,
    originalPrice: 124.0,
    discount: 17,
    rating: 4.8,
    reviewCount: 167,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 26,
    name: 'Babyjoy Twin Pack Size 5 Junior 12-18 Kg 2x58 Diapers',
    brand: 'Babyjoy',
    price: 135.5,
    originalPrice: 165.0,
    discount: 18,
    rating: 4.6,
    reviewCount: 189,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 27,
    name: 'Fine Baby Mega Pack Size 6 XXL 16+ Kg 54 Diapers',
    brand: 'Fine Baby',
    price: 71.99,
    originalPrice: 87.0,
    discount: 17,
    rating: 4.5,
    reviewCount: 145,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 28,
    name: 'Huggies Natural Care Size 3 Midi 5-9 Kg 78 Diapers',
    brand: 'Huggies',
    price: 83.5,
    originalPrice: 101.0,
    discount: 17,
    rating: 4.6,
    reviewCount: 178,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 29,
    name: 'Pampers Pure Protection Size 2 Mini 4-8 Kg 88 Diapers',
    brand: 'Pampers',
    price: 93.99,
    originalPrice: 113.0,
    discount: 17,
    rating: 4.7,
    reviewCount: 212,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 30,
    name: 'Babyjoy Culotte Mega Pack Size 3 Medium 6-11 Kg 56 Pants',
    brand: 'Babyjoy',
    price: 67.5,
    originalPrice: 82.0,
    discount: 18,
    rating: 4.5,
    reviewCount: 156,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 31,
    name: 'Fine Baby Comfort Size 4 Large 7-14 Kg 62 Diapers',
    brand: 'Fine Baby',
    price: 64.99,
    originalPrice: 78.0,
    discount: 17,
    rating: 4.4,
    reviewCount: 134,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 32,
    name: 'Huggies Overnight Size 5 Junior 12-22 Kg 48 Diapers',
    brand: 'Huggies',
    price: 89.5,
    originalPrice: 108.0,
    discount: 17,
    rating: 4.7,
    reviewCount: 189,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 33,
    name: 'Pampers Splashers Size 4 Maxi 9-15 Kg 24 Swim Pants',
    brand: 'Pampers',
    price: 45.99,
    originalPrice: 55.0,
    discount: 16,
    rating: 4.5,
    reviewCount: 123,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 34,
    name: 'Bambi Sensitive Size 3 Midi 4-9 Kg 72 Diapers',
    brand: 'Bambi',
    price: 96.5,
    originalPrice: 116.0,
    discount: 17,
    rating: 4.6,
    reviewCount: 167,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 35,
    name: 'Babyjoy Super Soft Size 2 Mini 3-6 Kg 96 Diapers',
    brand: 'Babyjoy',
    price: 73.99,
    originalPrice: 90.0,
    discount: 18,
    rating: 4.5,
    reviewCount: 145,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 36,
    name: 'Fine Baby Ultra Thin Size 5 Junior 11-18 Kg 52 Diapers',
    brand: 'Fine Baby',
    price: 66.5,
    originalPrice: 80.0,
    discount: 17,
    rating: 4.4,
    reviewCount: 134,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 37,
    name: 'Huggies Gentle Care Size 1 Newborn 2-5 Kg 90 Diapers',
    brand: 'Huggies',
    price: 78.99,
    originalPrice: 95.0,
    discount: 17,
    rating: 4.6,
    reviewCount: 178,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 38,
    name: 'Pampers Harmonie Size 3 Midi 6-10 Kg 66 Diapers',
    brand: 'Pampers',
    price: 97.5,
    originalPrice: 117.0,
    discount: 17,
    rating: 4.7,
    reviewCount: 201,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 39,
    name: 'Babyjoy Premium Dry Size 4 Large 10-18 Kg 58 Diapers',
    brand: 'Babyjoy',
    price: 71.99,
    originalPrice: 87.0,
    discount: 17,
    rating: 4.5,
    reviewCount: 156,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }, {
    id: 40,
    name: 'Fine Baby Value Pack Size 4 Maxi 7-14 Kg 72 Diapers',
    brand: 'Fine Baby',
    price: 69.5,
    originalPrice: 84.0,
    discount: 17,
    rating: 4.4,
    reviewCount: 134,
    image: 'https://magento-test.innovaksa.net/media/catalog/product/1/0/1000587_1_1.jpg?width=256&quality=100&format=webp'
  }];
  const nowAvailableProducts = isNowContext ? products.filter(product => {
    if (nowAvailability === 'limited') return product.id % 5 === 0;
    if (nowAvailability === 'available') return product.id % 5 !== 0;
    return true;
  }) : products;
  const visibleProducts = nowAvailableProducts.slice(0, displayedCount);
  const hasMoreProducts = visibleProducts.length < nowAvailableProducts.length;
  useEffect(() => {
    setDisplayedCount(INITIAL_PRODUCT_COUNT);
  }, [categoryName, nowAvailability, nowCategoryId, nowSubcategory, selectedFilters, sortBy]);
  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || !hasMoreProducts) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setDisplayedCount(current => Math.min(current + PRODUCT_LOAD_INCREMENT, nowAvailableProducts.length));
      }
    }, {
      rootMargin: '320px 0px'
    });
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMoreProducts, nowAvailableProducts.length]);
  return <div className="min-h-screen bg-gray-50 pt-[var(--header-height-mobile)] md:pt-0" data-id="element-6523" data-mp="241zcxh6kbkdq">
      <MobileInternalHeader title={isNowContext ? pharmacyCategories.find(category => category.id === nowCategoryId)?.name ?? categoryName : categoryName} data-id="element-6524" data-mp="jhlwcjhl4yxa" />
      {isNowContext && <NowCategoryBrowseHeader activeCategoryId={nowCategoryId} activeSubcategory={nowSubcategory} onCategorySelect={categoryId => {
      setNowCategoryId(categoryId);
      setNowSubcategory('All');
    }} onSubcategorySelect={setNowSubcategory} data-id="element-6525" data-mp="thcseplsnlzh" />}
      {!isNowContext && <>
      {/* Banner Row - 16:9 aspect ratio */}
      <div className="container mx-auto px-4 md:px-8 lg:px-12 py-1 md:py-4 pt-2 md:pt-4" data-id="element-6526" data-mp="5qp5oy77yrsv">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-6527" data-mp="9qhijn3nbk41">
          {banners.map((banner, index) => <a key={index} href={banner.link} className="block rounded-xl overflow-hidden bg-gray-300 hover:shadow-md transition-shadow relative" data-id="element-6528" data-mp="ek28crv357s5" data-mpi={index}>
              <div className="relative w-full" style={{
              paddingTop: '56.25%'
            }} data-id="element-6529" data-mp="3i613zzy2ry8j">
                <div className="absolute inset-0 flex flex-col justify-center p-6 md:p-8 text-gray-800" data-id="element-6530" data-mp="kd99v1q8c110">
                  <span className="text-xs font-bold bg-white text-gray-600 px-3 py-1 rounded-full w-max mb-3" data-id="element-6531" data-mp="z1vmgvoh2eoj">
                    DIMENSIONS
                  </span>
                  <h2 className="text-2xl md:text-3xl font-bold mb-2" data-id="element-6532" data-mp="6dzs3xmdkyux">
                    BANNER SIZE
                  </h2>
                  <p className="text-sm md:text-base font-medium mb-3" data-id="element-6533" data-mp="1ggemzxi4q41">
                    50% width (desktop) | 100% width (mobile)
                  </p>
                  <button className="bg-white hover:bg-gray-100 text-gray-700 font-bold py-2 px-4 rounded-md w-max text-sm" data-id="element-6534" data-mp="bdm6wbhuqn5u">
                    Ratio 16:9
                  </button>
                </div>
              </div>
            </a>)}
        </div>
      </div>
      {/* 6 Banner Row - 3:1 aspect ratio */}
      <div className="container mx-auto px-4 md:px-8 lg:px-12 pb-1 md:pb-4" data-id="element-6535" data-mp="1ubr34dz5rtmj">
        {/* Desktop View - 6 banners in a row */}
        <div className="hidden md:grid md:grid-cols-6 gap-3" data-id="element-6536" data-mp="1zc5090z81j5s">
          {[1, 2, 3, 4, 5, 6].map(index => <a key={index} href="#" className="rounded-lg overflow-hidden relative bg-gray-300 transform transition-transform hover:scale-105" data-id="element-6537" data-mp="1kfnsejq8qmnz">
              <div className="w-full h-32" data-id="element-6538" data-mp="2d3a30bxvyrkc"></div>
              <div className="absolute inset-0 flex flex-col justify-center p-3 text-gray-800" data-id="element-6539" data-mp="2i1lcumo6l22k">
                <span className="text-xs font-bold bg-white text-gray-600 px-2 py-0.5 rounded-full w-max mb-2" data-id="element-6540" data-mp="23d8exk5j995f">
                  DIMENSIONS
                </span>
                <h3 className="text-sm font-bold mb-1" data-id="element-6541" data-mp="289yvs9p5w8rw">1/3 WIDTH BANNER</h3>
                <p className="text-xs font-medium" data-id="element-6542" data-mp="2w0umt4lsmzy3">ASPECT RATIO 3:1</p>
              </div>
            </a>)}
        </div>
        {/* Mobile View - Horizontal Scroll */}
        <div className="md:hidden flex overflow-x-auto snap-x snap-mandatory hide-scrollbar gap-3 pb-2 -mx-4 px-4" data-id="element-6543" data-mp="1d4azv5s6cx5n">
          {[1, 2, 3, 4, 5, 6].map(index => <a key={index} href="#" className="flex-none w-[70vw] snap-center rounded-lg overflow-hidden relative bg-gray-300" data-id="element-6544" data-mp="135vb39gmdzzb">
              <div className="w-full h-32" data-id="element-6545" data-mp="y8v0cl8ts91v"></div>
              <div className="absolute inset-0 flex flex-col justify-center p-4 text-gray-800" data-id="element-6546" data-mp="1vusiuin7gedt">
                <span className="text-[10px] font-bold bg-white text-gray-600 px-2 py-0.5 rounded-full w-max mb-1.5" data-id="element-6547" data-mp="1qv62i0c2yi0s">
                  DIMENSIONS
                </span>
                <h3 className="text-sm font-bold mb-0.5" data-id="element-6548" data-mp="1h5mll07xgy8k">1/3 WIDTH BANNER</h3>
                <p className="text-xs font-medium" data-id="element-6549" data-mp="1h3juecv1gz29">ASPECT RATIO 3:1</p>
              </div>
            </a>)}
        </div>
      </div>
        </>}
      {/* Filters + Sort + Product Grid */}
      <div className="container mx-auto px-4 md:px-8 lg:px-12 pb-12 md:pb-12 pb-32" data-id="element-6550" data-mp="29sgz1d6buxcj">
        {/* Mobile Results Count Bar */}
        {!isNowContext && <div className="md:hidden py-3 px-4 flex items-center justify-between -mx-4 mb-2 bg-white border-b border-gray-100" data-id="element-6551" data-mp="2m5kqy6ipd4z3">
          <div className="text-sm font-bold text-gray-900" data-id="element-6552" data-mp="2r21h169gptet">
            {products.length} products
          </div>
          <div className="text-xs text-gray-500 font-medium" data-id="element-6553" data-mp="2w0meixxbxyzk">
            Results: {visibleProducts.length === 0 ? 0 : 1}-{visibleProducts.length}
          </div>
        </div>}

        {/* Active Filter Chips Row (Mobile Only) */}
        {activeFilters.length > 0 && <div className="md:hidden flex overflow-x-auto hide-scrollbar gap-2 mb-4 -mx-4 px-4 pb-1" data-id="element-6554" data-mp="39vf1ghfjuhg6">
            {activeFilters.map(filter => <button key={filter.id} onClick={() => removeFilter(filter)} className="flex-none flex items-center gap-1 px-3 py-1.5 bg-blue-50 border border-blue-100 text-[#0066CC] rounded-full text-sm font-medium whitespace-nowrap" data-id="element-6555" data-mp="3jsa2p837ivy5">
                {filter.label}
                <XIcon className="w-3.5 h-3.5" data-id="element-6556" data-mp="2u4xkkky50spu" />
              </button>)}
            <button onClick={clearAllFilters} className="flex-none px-3 py-1.5 text-gray-500 text-sm font-medium underline whitespace-nowrap" data-id="element-6557" data-mp="38tk99d9nmwtq">
              Clear all
            </button>
          </div>}

        {/* Desktop Layout: Sidebar + Grid */}
        <div className="flex gap-6" data-id="element-6558" data-mp="3cv5j0pqw6uzp">
          {/* Desktop Filter Sidebar */}
          <div className="hidden md:block w-64 lg:w-72 flex-shrink-0" data-id="element-6559" data-mp="3rrwh8voty0nq">
            <div className="sticky top-24" data-id="element-6560" data-mp="3rr4vsdoy5jb6">
              <FilterSidebar selectedFilters={selectedFilters} onFilterChange={setSelectedFilters} hideCategories={isNowContext} data-id="element-6561" data-mp="3msa7iw87n2jo" />
            </div>
          </div>
          {/* Product Grid */}
          <div className="flex-1" data-id="element-6562" data-mp="1sgvfg8rq8r0r">
            {/* Results count and sort */}
            {!isNowContext && <div className="flex items-center justify-between mb-6 bg-white p-4 rounded-lg" data-id="element-6563" data-mp="7z44h562yazw">
              <p className="text-sm text-gray-600" data-id="element-6564" data-mp="cxnqt6ol5oss">
                Results:{' '}
                <span className="font-medium text-gray-900" data-id="element-6565" data-mp="3uc2nxb4tw57z">
                  {visibleProducts.length === 0 ? 0 : 1}-{visibleProducts.length}
                </span>{' '}
                of{' '}
                <span className="font-medium text-gray-900" data-id="element-6566" data-mp="3lacvq334h295">
                  {nowAvailableProducts.length}
                </span>
              </p>
              {/* Desktop sort dropdown */}
              <div className="hidden md:flex items-center gap-2" data-id="element-6567" data-mp="3bf1gzkrprv1e">
                <span className="text-sm text-gray-600" data-id="element-6568" data-mp="3gbrv42jykgk6">Sort By</span>
                <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0066CC]" data-id="element-6569" data-mp="19m3t83csnsnb">
                  <option value="best-match" data-id="element-6570" data-mp="1dk536fkuwtf">Recommended</option>
                  <option value="top-rated" data-id="element-6571" data-mp="3tj79ho9i639f">Top Rated</option>
                  <option value="price-low-high" data-id="element-6572" data-mp="3orfso5g5fbwh">Price: Low to High</option>
                  <option value="price-high-low" data-id="element-6573" data-mp="3opbfsy09v2k2">Price: High to Low</option>
                  <option value="newest" data-id="element-6574" data-mp="3jrtg7y859zoq">Newest</option>
                </select>
              </div>
              </div>}
            {/* Product grid switches to the compact NOW 120 card only when entered from NOW. */}
            <div className={`grid gap-2 md:gap-4 mb-8 ${isNowContext ? 'grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6' : 'grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'}`} data-id="element-6575" data-mp="39wi0pdowwofx">
              {visibleProducts.map(product => isNowContext ? <NowCompactProductCard key={product.id} product={product} data-id="element-6576" data-mp="140fatuttqmdq" /> : <EnhancedProductCard key={product.id} product={product} data-id="element-6577" data-mp="z3f3zhp5hflk" />)}
            </div>
            {isNowContext && visibleProducts.length === 0 && <div className="mb-8 rounded-xl border border-brand-border bg-white p-6 text-center" data-id="element-6578" data-mp="1bk6i0p4n57ct">
                <p className="text-sm font-bold text-brand-ink" data-id="element-6579" data-mp="1gb6b1jan35a1">No products match this availability filter.</p>
                <button type="button" onClick={() => setNowAvailability('all')} className="mt-3 text-sm font-bold text-brand" data-id="element-6580" data-mp="1l9hk3rt41br7">Show all nearby products</button>
              </div>}
            {hasMoreProducts && <div ref={loadMoreRef} className="flex h-12 items-center justify-center" aria-live="polite" data-id="element-6581" data-mp="smn4b878n0k2"><span className="sr-only" data-id="element-6582" data-mp="xjvkj38tsuio">Loading more products</span></div>}
          </div>
        </div>
      </div>

      {/* Floating Filter & Sort FAB (Mobile Only) */}
      <AnimatePresence data-id="element-6583" data-mp="290dix8vzh77y">
        {showFab && <motion.div initial={{
        y: 100,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 100,
        opacity: 0
      }} className="md:hidden fixed bottom-[72px] left-0 right-0 mx-auto w-max z-40 flex items-center bg-brand text-white rounded-xl shadow-2xl overflow-hidden" data-id="element-6584" data-mp="28zk91z1qa1ll">
            <button onClick={() => setShowMobileSort(true)} className="flex items-center justify-center gap-1.5 px-3.5 py-2.5 border-r border-white/20 active:bg-white/10 transition-colors" data-id="element-6585" data-mp="2zci6l0xe95pi">
              <ArrowUpDownIcon className="w-3.5 h-3.5" data-id="element-6586" data-mp="2qcv74ko869z3" />
              <span className="text-[11px] font-bold uppercase tracking-wide" data-id="element-6587" data-mp="2beuimbpof0ez">
                Sort
              </span>
            </button>
            <button onClick={() => setShowMobileFilters(true)} className="flex items-center justify-center gap-1.5 px-3.5 py-2.5 active:bg-white/10 transition-colors relative" data-id="element-6588" data-mp="2gep6e6e5n9vt">
              <FilterIcon className="w-3.5 h-3.5" data-id="element-6589" data-mp="elrkkuisphkd" />
              <span className="text-[11px] font-bold uppercase tracking-wide" data-id="element-6590" data-mp="1z4244o87d9ml">
                Filter
              </span>
              {activeFilters.length > 0 && <span className="absolute top-1.5 right-2 bg-red-500 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center border border-[#0066CC]" data-id="element-6591" data-mp="1u3ntqzj4pnle">
                  {activeFilters.length}
                </span>}
            </button>
          </motion.div>}
      </AnimatePresence>

      {/* Mobile Filter Modal */}
      <MobileFilterModal isOpen={showMobileFilters} onClose={() => setShowMobileFilters(false)} selectedFilters={selectedFilters} onFilterChange={setSelectedFilters} hideCategories={isNowContext} data-id="element-6592" data-mp="2dsy5k2fr7oe8" />
      {/* Mobile Sort Modal */}
      <MobileSortModal isOpen={showMobileSort} onClose={() => setShowMobileSort(false)} sortBy={sortBy} onSortChange={setSortBy} data-id="element-6593" data-mp="xgasd3lyj2ki" />
    </div>;
}